import { useForm } from '@sequencehq/utils'
import { isEmail, isUrl, required } from '@sequencehq/utils/validation'
import { useNotifications } from '@sequencehq/core-components'
import { useApiQueryClient, useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

export type Merchant = NonNullable<
  Awaited<
    ReturnType<typeof dashboard20240730Client.getMerchantForSequenceAccount>
  >['data']
>

const addressFields = [
  'line1',
  'line2',
  'postcode',
  'town',
  'state',
  'country'
] as const

type AddressField = (typeof addressFields)[number]

function isAddressField(field: string): field is AddressField {
  return addressFields.includes(field as AddressField)
}

type MutableAllowedField =
  | 'legalCompanyName'
  | 'email'
  | 'appUrl'
  | 'supportEmail'
  | 'supportUrl'
  | AddressField

type OmitUnion<T, U extends T> = T extends U ? never : T
type PickUnion<T, U extends T> = U

export const useMerchantSettings = (merchant: Merchant) => {
  const { displayNotification } = useNotifications()

  const queryClient = useApiQueryClient()

  const merchantMutator = useMutation(dashboard20240730Client.putMerchant, {
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: dashboard20240730Client.getMerchantForSequenceAccount.queryKey
      })
    }
  })

  const { fields, queries } = useForm({
    value: {
      legalCompanyName: merchant.legalCompanyName,
      email: merchant.email,
      appUrl: merchant.appUrl,
      supportEmail: merchant.supportEmail,
      supportUrl: merchant.supportUrl,
      line1: merchant.address.line1,
      line2: merchant.address.line2,
      country: merchant.address.country,
      state: merchant.address.state,
      town: merchant.address.town,
      postcode: merchant.address.postcode
    },
    showValidationErrors: true,
    fieldConfiguration: [
      {
        property: 'legalCompanyName',
        validation: [required]
      },
      {
        property: 'email',
        validation: [required, isEmail]
      },
      {
        property: 'appUrl',
        validation: [isUrl]
      },
      {
        property: 'supportEmail',
        validation: [isEmail]
      },
      {
        property: 'supportUrl',
        validation: [isUrl]
      },
      {
        property: 'line1',
        validation: [required]
      },
      {
        property: 'line2',
        validation: []
      },
      {
        property: 'postcode',
        validation: [required]
      },
      {
        property: 'town',
        validation: [required]
      },
      {
        property: 'state',
        validation: []
      },
      {
        property: 'country',
        validation: [required]
      }
    ]
  })

  const mutate = (
    field: MutableAllowedField,
    { label, value }: { label: string; value?: string }
  ) => {
    const newValue = value ?? queries.formData[field]

    const isAddress = isAddressField(field)

    const prevValue = isAddress ? merchant.address[field] : merchant[field]

    if (newValue === prevValue || fields[field].validationErrors.length > 0) {
      return
    }

    const newFields = isAddress
      ? {
          address: {
            ...merchant.address,
            [field]: newValue
          }
        }
      : {
          address: merchant.address,
          [field]: newValue
        }

    merchantMutator.mutate(
      {
        id: merchant.id,
        body: {
          bankAccountDetails: merchant.bankAccountDetails,
          legalCompanyName: merchant.legalCompanyName,
          email: merchant.email,
          ...newFields
        }
      },
      {
        onSuccess: () => {
          displayNotification(`${label} updated`, {
            type: 'success'
          })
        },
        onError: () => {
          displayNotification(`${label} update failed`, {
            type: 'error'
          })
        }
      }
    )
  }

  const getInputFieldProps = (
    field: OmitUnion<keyof typeof fields, 'state' | 'country'>,
    opts: {
      id: string
      placeholder: string
      label: string
    }
  ) => ({
    id: opts.id,
    placeholder: opts.placeholder,

    disabled: merchantMutator.isPending,
    value: fields[field].value,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      fields[field].onChange(e.target.value),
    validationErrors: fields[field].validationErrors,
    onBlur: () => {
      mutate(field, {
        label: opts.label
      })
    }
  })

  const getSelectFieldProps = (
    field: PickUnion<keyof typeof fields, 'country' | 'state'>,
    opts: {
      label: string
      id: string
      placeholder: string
    }
  ) => ({
    disabled: merchantMutator.isPending,
    initialValue: fields[field].value,
    id: opts.id,
    placeholder: opts.placeholder,
    onChange: (value: any) => {
      fields[field].onChange(value)
      mutate(field, {
        label: opts.label,
        value
      })
    }
  })

  return {
    fields,
    getInputFieldProps,
    getSelectFieldProps
  }
}
